import { FormField, InputSelectField } from '@tempmee/react';
import { forwardRef, memo, type Ref, useMemo } from 'react';
import { Row, Text } from '~/commonComponents';
import { useAverageRate } from '~/domain/Jobs';
import { UserProfession } from '~/enums/UserProfession';
import greenCircleCheck from '~/images/green-circle-check.svg';
import warningAlertRed from '~/images/warning_alert.svg';
import warningAlertOrange from '~/images/warning_alert_orange.svg';
import { Colors } from '~/themes/colors';

interface IProfessionalPayFieldProps {
  value?: number | null;
  onChange?: (value: number) => void;
  professionType: string;
  officeId: string;
  hasError?: boolean;
}

const getRateStatus = (rate: number, averageRate: number) => {
  const percentage = (rate / averageRate) * 100;

  if (percentage >= 106) {
    return {
      status: 'More likely to fill.',
      message: 'Rate is competitive with the industry average',
      color: Colors.success_500,
      icon: greenCircleCheck,
    };
  }
  if (percentage >= 100) {
    return {
      status: 'Likely to fill.',
      message: 'Based on statewide compensation data.',
      color: Colors.success_500,
      icon: greenCircleCheck,
    };
  }
  if (percentage >= 85) {
    return {
      status: 'Less likely to fill.',
      message: 'Rate is below industry average',
      color: Colors.warning_700,
      icon: warningAlertOrange,
    };
  }
  return {
    status: 'Unlikely to fill.',
    message: 'Rate is well below industry average',
    color: Colors.error_500,
    icon: warningAlertRed,
  };
};

const Component = (
  { value, onChange, professionType, officeId, hasError }: IProfessionalPayFieldProps,
  ref: Ref<HTMLDivElement>,
) => {
  const { data: averageRating } = useAverageRate(officeId);

  const professionTypeCode = UserProfession.getCode(professionType) as string | undefined;
  const profRate = (professionTypeCode && averageRating?.[professionTypeCode]) || {
    average: 49,
    min: 18,
    max: 100,
  };

  const averageRate = Math.round(profRate.average);

  const rateOptions = useMemo(() => {
    const result = [];
    for (let i = profRate.min; i <= profRate.max; i++) {
      const isAverage = i === averageRate;
      result.push({
        label: isAverage ? `$${i}.00 /hour - Average` : `$${i}.00 /hour`,
        value: i,
      });
    }
    return result;
  }, [profRate.min, profRate.max, averageRate]);

  const rateStatus = value != null ? getRateStatus(value, averageRate) : null;

  return (
    <div ref={ref} data-testid="professional-pay-field" data-cy="professional-pay-field">
      <FormField label="Professional Pay">
        <InputSelectField<number>
          options={rateOptions}
          value={value ?? undefined}
          onChange={onChange}
          placeholder="$--.-- /hour"
          isError={Boolean(hasError)}
        />
      </FormField>

      {rateStatus && (
        <Row
          alignItems="flex-start"
          gap={6}
          data-testid="rate-status-message"
          data-cy="rate-status-message"
          marginTop={12}
          paddingLeft={20}
        >
          <img
            src={rateStatus.icon}
            alt="rate-status"
            data-testid="rate-status-icon"
            data-cy="rate-status-icon"
          />
          <Text fontSize={16} color="neutral_500">
            <Text bold fontSize={16} style={{ color: rateStatus.color }}>
              {rateStatus.status}
            </Text>{' '}
            {rateStatus.message}
          </Text>
        </Row>
      )}
    </div>
  );
};

export const ProfessionalPayField = memo(forwardRef(Component));
